import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';
import { map, tap, switchMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

import { AuthUserActions } from './auth-user.actions';
import { AuthClient, LoginRequest } from 'src/api-service';

@Injectable()
export class AuthUserEffects {
    private actions$ = inject(Actions);
    private router = inject(Router);
    private authClient = inject(AuthClient);

    initializePublicAuthentication$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthUserActions.initializePublicAuthentication),
            switchMap(({ username, password }) => {
                if (username && password) {
                    const request: LoginRequest = { userName: username, password };
                    return this.authClient.login(request).pipe(
                        map(response => AuthUserActions.initializePublicAuthenticationSuccess({ response })),
                        catchError(error =>
                            of(
                                AuthUserActions.initializePublicAuthenticationFailure({
                                    error: error.message || 'Failed to authorize user',
                                }),
                            ),
                        ),
                    );
                } else {
                    return of(
                        AuthUserActions.initializePublicAuthenticationFailure({
                            error: 'Invalid credentials',
                        }),
                    );
                }
            }),
        ),
    );

    validatePublicAuthentication$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthUserActions.validatePublicAuthentication),
            switchMap(() => {
                return this.authClient.validatePublicUserAuthentication().pipe(
                    map(response => AuthUserActions.initializePublicAuthenticationSuccess({ response })),
                    catchError(error => {
                        if (error.status === 401) {
                            return of(AuthUserActions.validatePublicAuthenticationSuccess());
                        }
                        return of(
                            AuthUserActions.validatePublicAuthenticationFailure({
                                error: error.message || 'Failed to authorize user',
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    redirectAfterStandardLogin$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AuthUserActions.initializePublicAuthenticationSuccess),
                tap(() => {
                    // Use the pending navigation if there is one (refresh can race the guard redirect)
                    const navigation = this.router.currentNavigation();
                    const url = navigation?.finalUrl ?? navigation?.extractedUrl ?? this.router.parseUrl(this.router.url);

                    // Only redirect from the login page – otherwise the user is already on (or heading to) the right page
                    const isLoginPage = url.root.children['primary']?.segments[0]?.path === 'login';
                    if (!isLoginPage) return;

                    this.router.navigateByUrl(url.queryParams['returnUrl'] ?? '/');
                }),
            ),
        { dispatch: false },
    );

    customerLogout$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AuthUserActions.customerLogout),
                switchMap(() => {
                    return this.authClient.logout().pipe(
                        tap(() => {
                            this.router.navigate(['/login']);
                        }),
                        catchError(error => {
                            console.error('Logout error:', error);
                            this.router.navigate(['/login']);
                            return of(null);
                        }),
                    );
                }),
            ),
        { dispatch: false },
    );
}
